<!--
  MagicButton — primary CTA with shimmer sweep + gradient border.
  Style inspired by 21st-style animated buttons (shimmer + soft glow), hand-rolled
  CSS so we own the implementation.

  Usage:
    <MagicButton onclick={…} variant="primary" disabled={…}>
      <ZapIcon size={16} /> Generate
    </MagicButton>

  Variants: primary (blue glow), success (green), ghost (transparent), danger (red).
-->
<script lang="ts">
  type Variant = 'primary' | 'success' | 'ghost' | 'danger' | 'clay';

  interface Props {
    variant?: Variant;
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    loading?: boolean;
    onclick?: (e: MouseEvent) => void;
    title?: string;
    type?: 'button' | 'submit';
    children?: import('svelte').Snippet;
  }
  let {
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    onclick,
    title,
    type = 'button',
    children
  }: Props = $props();

  function handle(e: MouseEvent) {
    if (disabled || loading) return;
    onclick?.(e);
  }
</script>

<button
  {type}
  class="magic-btn {variant} size-{size}"
  class:loading
  disabled={disabled || loading}
  onclick={handle}
  {title}
>
  <span class="content">
    {#if children}{@render children()}{/if}
  </span>
  <span class="shimmer" aria-hidden="true"></span>
  <span class="glow" aria-hidden="true"></span>
</button>

<style>
  .magic-btn {
    --c1: #2e96ff;
    --c2: #4ab8ff;
    --glow-color: rgba(74, 184, 255, 0.65);
    position: relative;
    display: inline-flex;
    align-items: center; justify-content: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.08);
    background: linear-gradient(180deg, var(--c1), color-mix(in oklab, var(--c1) 75%, black));
    color: #fff;
    font-family: inherit;
    font-size: 13px; font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    overflow: hidden;
    isolation: isolate;
    transition: transform 0.12s ease, box-shadow 0.18s ease, border-color 0.18s ease;
    box-shadow:
      0 1px 0 rgba(255,255,255,0.10) inset,
      0 0 0 1px rgba(255,255,255,0.04),
      0 6px 18px -8px var(--glow-color);
  }
  .magic-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(255,255,255,0.18);
    box-shadow:
      0 1px 0 rgba(255,255,255,0.12) inset,
      0 0 0 1px rgba(255,255,255,0.06),
      0 14px 30px -10px var(--glow-color);
  }
  .magic-btn:active:not(:disabled) { transform: translateY(0); }
  .magic-btn:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

  /* Shimmer sweep — diagonal highlight that crosses the button on hover. */
  .shimmer {
    position: absolute; inset: 0;
    background: linear-gradient(110deg,
      transparent 30%,
      rgba(255,255,255,0.22) 48%,
      rgba(255,255,255,0.35) 50%,
      rgba(255,255,255,0.22) 52%,
      transparent 70%);
    transform: translateX(-110%);
    transition: transform 0.85s cubic-bezier(0.2, 0.7, 0.1, 1);
    pointer-events: none;
    mix-blend-mode: overlay;
  }
  .magic-btn:hover:not(:disabled) .shimmer { transform: translateX(110%); }

  /* Outer glow ring that pulses subtly while idle — draws the eye. */
  .glow {
    position: absolute; inset: -2px;
    border-radius: 12px;
    background: radial-gradient(60% 80% at 50% 100%, var(--glow-color), transparent 70%);
    opacity: 0.0;
    z-index: -1;
    transition: opacity 0.3s ease;
  }
  .magic-btn:hover:not(:disabled) .glow { opacity: 0.7; }
  .magic-btn.loading .glow { opacity: 0.5; animation: pulse 1.4s ease-in-out infinite; }
  @keyframes pulse { 50% { opacity: 0.9; } }

  .content { display: inline-flex; align-items: center; gap: 6px; position: relative; z-index: 1; }

  .size-sm { padding: 6px 12px; font-size: 11.5px; border-radius: 8px; }
  .size-lg { padding: 13px 22px; font-size: 14px; border-radius: 12px; }

  .success {
    --c1: #1f9d6f;
    --c2: #34d399;
    --glow-color: rgba(52, 211, 153, 0.6);
  }
  .ghost {
    background: rgba(255,255,255,0.05);
    color: var(--text-1, #e6eef6);
    border-color: rgba(255,255,255,0.1);
    --glow-color: rgba(255,255,255,0.1);
  }
  .ghost:hover:not(:disabled) { background: rgba(255,255,255,0.1); }
  .danger {
    --c1: #c84134;
    --c2: #f04a3a;
    --glow-color: rgba(240, 74, 58, 0.55);
  }
  .clay {
    --c1: #d6803a;
    --c2: #f59e4b;
    --glow-color: rgba(245, 158, 75, 0.6);
  }

  @media (prefers-reduced-motion: reduce) {
    .magic-btn, .shimmer, .glow { transition: none; animation: none; }
  }
</style>
